import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { adminFetch } from '@/lib/apiClient';
import { idempotencyKeyFor } from '@/lib/http-core';
import type { EverTvVideo, EverTvVideoPayload } from '../types/everTvVideo';

const BASE = '/admin/v1/cms/ever-tv-videos';

// 관리자 목록은 Spring Page 가 아닌 plain array (`data[]`) — meta.pagination 없음.
interface ListEnvelope {
  success: boolean;
  data?: EverTvVideo[] | null;
}

interface SingleEnvelope {
  success: boolean;
  data?: EverTvVideo | null;
}

export const everTvVideosQueryKey = () => ['admin', 'ever-tv-videos'] as const;

export const everTvVideoQueryKey = (videoId: string) => ['admin', 'ever-tv-video', videoId] as const;

const fetchVideos = async (): Promise<EverTvVideo[]> => {
  const res = await adminFetch(BASE);
  if (!res.ok) throw new Error(`ever-tv videos list failed: ${res.status}`);
  const env = (await res.json()) as ListEnvelope;
  return env.data ?? [];
};

const fetchVideo = async (videoId: string): Promise<EverTvVideo> => {
  const res = await adminFetch(`${BASE}/${videoId}`);
  if (!res.ok) throw new Error(`ever-tv video fetch failed: ${res.status}`);
  const env = (await res.json()) as SingleEnvelope;
  if (!env.data) throw new Error('no data');
  return env.data;
};

export const useEverTvVideosQuery = () =>
  useQuery<EverTvVideo[]>({
    queryKey: everTvVideosQueryKey(),
    queryFn: fetchVideos,
  });

export const useEverTvVideoQuery = (videoId: string | null) =>
  useQuery<EverTvVideo>({
    queryKey: everTvVideoQueryKey(videoId ?? ''),
    queryFn: () => fetchVideo(videoId as string),
    enabled: videoId != null,
  });

export const useEverTvVideoMutation = () => {
  const qc = useQueryClient();
  const invalidateList = () => qc.invalidateQueries({ queryKey: ['admin', 'ever-tv-videos'] });

  const create = useMutation({
    mutationFn: async (payload: EverTvVideoPayload): Promise<EverTvVideo> => {
      const res = await adminFetch(BASE, {
        method: 'POST',
        headers: { 'Idempotency-Key': idempotencyKeyFor('POST', BASE, payload) },
        body: JSON.stringify(payload),
      });
      if (!res.ok) throw new Error(`ever-tv video create failed: ${res.status}`);
      const env = (await res.json()) as SingleEnvelope;
      if (!env.data) throw new Error('no data');
      return env.data;
    },
    onSuccess: invalidateList,
  });

  const update = useMutation({
    mutationFn: async ({
      videoId,
      payload,
    }: {
      videoId: string;
      payload: EverTvVideoPayload;
    }): Promise<EverTvVideo> => {
      const path = `${BASE}/${videoId}`;
      const res = await adminFetch(path, {
        method: 'PUT',
        headers: { 'Idempotency-Key': idempotencyKeyFor('PUT', path, payload) },
        body: JSON.stringify(payload),
      });
      if (!res.ok) throw new Error(`ever-tv video update failed: ${res.status}`);
      const env = (await res.json()) as SingleEnvelope;
      if (!env.data) throw new Error('no data');
      return env.data;
    },
    onSuccess: (_, { videoId }) => {
      void qc.invalidateQueries({ queryKey: everTvVideoQueryKey(videoId) });
      invalidateList();
    },
  });

  const remove = useMutation({
    mutationFn: async (videoId: string): Promise<void> => {
      const path = `${BASE}/${videoId}`;
      const res = await adminFetch(path, {
        method: 'DELETE',
        headers: { 'Idempotency-Key': idempotencyKeyFor('DELETE', path, '') },
      });
      if (!res.ok) throw new Error(`ever-tv video delete failed: ${res.status}`);
    },
    onSuccess: invalidateList,
  });

  return { create, update, remove };
};
